import { ChangeDetectionStrategy, Component, DOCUMENT, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivityEntityName, Attachment, SOR_EVIDENCE_BUCKETS, SOR_EVIDENCE_BUCKET_LABELS, SorEvidenceBucket } from '@jms/model';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { take } from 'rxjs';
import { AllowedPickType, FilesDashboardComponent, FilesDisplayMode, type FilesContext } from '@workbuddy/ui/modules/files';
import { UploadAllowedType, UploadFieldComponent } from '@workbuddy/ui/fields/upload';
import { WorkBuddyDialogService } from '@workbuddy/ui/services';
import { PAGE_COMPONENTS } from '@workbuddy/ui/blocks/page';
import { ButtonComponent } from '@workbuddy/ui/components/button';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { itemEvidenceBulkDialogBridgeStyles, itemEvidenceBulkDialogStyleId } from './item.evidence.bulk.dialog.theme';

export interface ItemEvidenceBulkDialogData {
  title: string;
  /**
   * Job the related-files picker reads from, or null when the selection spans more than one job —
   * there is no single library to pick from then, so the related option is withheld rather than
   * silently offering one job's files for items on another.
   */
  job: { _id: string; name: string } | null;
  timezone?: string;
}

/** Buckets the operator filled. An untouched bucket is absent, so the server can tell "not asked for" from "empty". */
export type ItemEvidenceBulkResult = Partial<Record<SorEvidenceBucket, Attachment[]>>;

/**
 * Bulk "Add Photo / Video Evidence" composer for SOR items. Shows all three evidence buckets at once because the
 * operator is filling them for a whole selection: which bucket a given item actually accepts is decided
 * per row on the server, so pre-narrowing the dialog to the intersection would hide buckets that most of
 * the selection can take.
 *
 * Files upload ONCE here, and the server keeps that one attachment id on every row it stamps, so the
 * Files tab lists the photo once however many items received it. Apply stays disabled while an upload
 * is in flight: an attachment without a stored blob would be written to every item as a broken reference.
 */
@Component({
  selector: 'item-evidence-bulk-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ReactiveFormsModule, UploadFieldComponent, PAGE_COMPONENTS, ButtonComponent],
  // Not root-provided — the files picker opens through this component's own dialog service, same as the evidence rail.
  providers: [WorkBuddyDialogService],
  styleUrls: ['./item.evidence.bulk.dialog.component.less'],
  template: `
    <w-page [title]="title" mode="modal" (close)="onClose()">
      <ng-container *w-page-body-content>
        <div class="evidence-body">
          <div class="buckets" [formGroup]="form">
            @for (bucket of buckets; track bucket) {
              <section class="bucket">
                <h3 class="bucket-title">
                  {{ labels[bucket] }}
                  @if ($value()[bucket]?.length; as filled) {
                    <span class="bucket-count">{{ filled }} selected</span>
                  }
                </h3>

                <upload-field
                  appearance="compact"
                  [formControlName]="bucket"
                  [allowedTypes]="UploadAllowedType.ImagesAndVideos"
                  [maxUploadSize]="50"
                  [previewColumns]="4"
                  [maxVisiblePreviews]="8"
                  [hideAddRelated]="!data.job"
                  (addRelatedFiles)="onAddRelated($event)"
                  (uploading)="onUploading(bucket, $event)" />
              </section>
            }
          </div>

          <p class="buckets-note">Each item only receives the buckets it defines — the rest are reported as skipped.</p>
        </div>
      </ng-container>

      <ng-container *w-page-footer-right-actions>
        <w-button displayStyle="Primary" label="Apply" size="Medium" [disabled]="$applyDisabled()" (click)="onApply()" />
      </ng-container>
    </w-page>
  `,
})
export class ItemEvidenceBulkDialogComponent {

  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly dialogService = inject(WorkBuddyDialogService);
  private readonly builder = inject(FormBuilder);
  private readonly document = inject(DOCUMENT);

  protected readonly data = inject(DynamicDialogConfig).data as ItemEvidenceBulkDialogData;
  protected readonly title = this.data.title;

  protected readonly buckets = SOR_EVIDENCE_BUCKETS;
  protected readonly labels = SOR_EVIDENCE_BUCKET_LABELS;
  protected readonly UploadAllowedType = UploadAllowedType;

  protected readonly form = this.builder.group({
    beforePhoto: [[] as Attachment[]],
    duringPhoto: [[] as Attachment[]],
    afterPhoto: [[] as Attachment[]],
  });

  protected readonly $value = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });
  private readonly $uploading = signal<ReadonlySet<SorEvidenceBucket>>(new Set());

  private readonly $filled = computed(() => this.buckets.filter(bucket => !!this.$value()[bucket]?.length));

  protected readonly $applyDisabled = computed(() => !!this.$uploading().size || !this.$filled().length);

  public constructor() {

    DesignSystem.Mount(this.document, itemEvidenceBulkDialogStyleId, itemEvidenceBulkDialogBridgeStyles);
  }

  protected onUploading(bucket: SorEvidenceBucket, uploading: boolean): void {

    this.$uploading.update(current => {
      const next = new Set(current);
      if (uploading) next.add(bucket); else next.delete(bucket);
      return next;
    });
  }

  /**
   * Picks files already held on the job. `addRelated` republishes each pick into the field's own value,
   * so a related file and a fresh upload reach Apply as the same kind of attachment.
   */
  protected onAddRelated(upload: UploadFieldComponent): void {

    const job = this.data.job;
    if (!job) return;

    const context = {
      entity: ActivityEntityName.Job,
      entityId: job._id,
      entityName: job.name,
      displayMode: FilesDisplayMode.Picker,
      // No Video pick type exists — a clip on the job is stored as a File, so both are offered and the
      // upload field's own accept list is what keeps a document out of an evidence bucket.
      allowTypes: [AllowedPickType.Images, AllowedPickType.Files],
      timezone: this.data.timezone,
    } as FilesContext;

    this.dialogService.open(FilesDashboardComponent, { baseZIndex: 10000, data: { context } })
      .onClose
      .pipe(take(1))
      .subscribe((picked: Attachment[]) => {
        if (picked?.length) upload.addRelated(picked);
      });
  }

  protected onApply(): void {

    if (this.$applyDisabled()) return;

    const result: ItemEvidenceBulkResult = {};
    for (const bucket of this.$filled()) result[bucket] = this.form.controls[bucket].value ?? [];

    this.dialogRef.close(result);
  }

  protected onClose(): void {

    this.dialogRef.close();
  }
}
